import { useMemo, useState } from 'react'
import { Stack, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { entries, flatMap, groupBy as groupByData, pipe } from 'remeda'
import { commonDataGridStyles } from '~/ui/styles/data-grid'
import { startOfYear } from 'date-fns'
import { useGetVisitors } from '~/features/hooks/useGetVisitors'
import { OverviewCard } from '~/ui/components/OverviewCard'
import { IconShoppingBag, IconUserFilled } from '@tabler/icons-react'
import { useDateRangePicker } from '~/ui/hooks/useDateRangePicker'
import { useRole } from '~/features/auth/useRole'
import { getAgencyScopeOrFilter } from '~/features/auth/utils'

type GroupByType = 'agency' | 'country'

const now = new Date()
const startOfThisYear = startOfYear(now)

export const Reports = () => {
  const [groupBy, setGroupBy] = useState<GroupByType>('agency')

  const { DateRangePicker, dates } = useDateRangePicker()
  const { role } = useRole()

  const { data: visitors, isLoading: isVisitorsLoading } = useGetVisitors({
    filters:
      dates.start && dates.end
        ? [
            { operator: 'lt', value: dates.end.toISOString(), column: 'created_at' },
            { operator: 'gt', value: dates.start.toISOString(), column: 'created_at' },
            { column: 'has_refund', operator: 'eq', value: false },
          ]
        : [
            { column: 'created_at', operator: 'gt', value: startOfThisYear.toISOString() },
            { column: 'has_refund', operator: 'eq', value: false },
          ],
    // Marketing users only see google/instagram agencies; other roles see everything.
    orFilter: getAgencyScopeOrFilter(role),
  })

  const columns = useMemo<
    GridColDef<{
      name: string
      total: number
      count: number
      average: number
      percentage: number
      id: string
    }>[]
  >(() => {
    return [
      {
        field: 'name',
        headerName: groupBy === 'agency' ? 'Agency' : 'Country',
        width: 200,
      },
      {
        field: 'total',
        headerName: 'Total Sale',
        valueFormatter: (value: unknown) =>
          typeof value === 'number' ? value.toLocaleString('cs', { currency: 'CZK' }) : value,
        minWidth: 150,
      },
      {
        field: 'count',
        headerName: 'Visitor count',
        minWidth: 170,
      },
      {
        field: 'average',
        headerName: 'Sale per visitor',
        valueFormatter: (value: unknown) =>
          typeof value === 'number'
            ? value.toLocaleString('cs', {
                currency: 'CZK',
                maximumFractionDigits: 0,
              })
            : value,
        minWidth: 180,
      },
      {
        field: 'percentage',
        headerName: 'Percentage of Total',
        valueFormatter: (value: unknown) =>
          typeof value === 'number' ? `${value.toFixed(1)}%` : value,
        flex: 1,
        minWidth: 200,
      },
    ]
  }, [groupBy])

  const rows = useMemo<
    Array<{
      name: string
      total: number
      count: number
      average: number
      percentage: number
      id: string
    }>
  >(() => {
    if (!visitors) return []

    const grouped = groupByData(visitors, (visit) => visit[groupBy])

    const rowsData = pipe(
      entries(grouped),
      flatMap(([key, value], index) => {
        const total = value.reduce((acc, v) => (acc += v.total), 0)
        return {
          name: key,
          total,
          count: value.length,
          average: total / value.length,
          percentage: 0, // Will be calculated after totals
          id: index.toString(),
        }
      })
    )

    // Calculate percentages after we have all totals
    const overallTotal = rowsData.reduce((acc, row) => acc + row.total, 0)
    return rowsData.map((row) => ({
      ...row,
      percentage: overallTotal > 0 ? (row.total / overallTotal) * 100 : 0,
    }))
  }, [visitors, groupBy])

  const totals = useMemo<{ total: number; count: number }>(() => {
    return rows.reduce<{ total: number; count: number }>(
      (acc, row) => {
        acc.total += row.total
        acc.count += row.count
        return acc
      },
      { total: 0, count: 0 }
    )
  }, [rows])

  return (
    <Stack flex={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, md: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={1}
          sx={{ mt: { xs: 'unset', md: 'auto' } }}
        >
          <OverviewCard
            Icon={IconShoppingBag}
            value={
              <Typography>{totals.total.toLocaleString('cs', { currency: 'CZK' })}</Typography>
            }
            description="Total Sale"
            color="secondary"
          />
          <OverviewCard
            Icon={IconUserFilled}
            value={<Typography>{totals.count}</Typography>}
            description="Total Visitors"
            color="secondary"
          />
        </Stack>
        <Stack gap={1} sx={{ ml: { md: 'auto' }, alignItems: { md: 'flex-end' } }}>
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) {
                setGroupBy(newValue)
              }
            }}
            size="small"
          >
            <ToggleButton value="agency">Agency</ToggleButton>
            <ToggleButton value="country">Country</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2, ml: 'auto' }}>
        {DateRangePicker}
      </Stack>
      <Stack position="relative" flex={1}>
        <DataGrid
          columns={columns}
          rows={rows}
          loading={isVisitorsLoading}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: 'percentage', sort: 'desc' }],
            },
          }}
          sx={commonDataGridStyles}
        />
      </Stack>
    </Stack>
  )
}
